import React from "react";

import { exportBlobsToZip, type File } from "@hsl/core/documents/lib";
import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import type { FundDataWithType } from "@hsl/fund-explorer/metaSchemas";

import { Translate } from "../../../hooks/useTranslation";
import dayjs from "../../../lib/dayjs";
import Button from "../../Button";
import { useDocumentContext } from "../context";
import TextButton from "./TextButton";

const DownloadsSection = ({ fund }: { fund: FundDataWithType }) => {
    const { selectionActive, selectedDocumentURLs, markRead } =
        useDocumentContext();
    const { bulkDownloadEvent } = useYardActionEvents();
    const handleDownload = async (evt: React.MouseEvent) => {
        evt.stopPropagation();
        const documentBlobs = await Promise.all(
            selectedDocumentURLs.map((url) =>
                fetch(url.value).then(
                    (resp) =>
                        ({
                            name: url.name,
                            data: resp.status === 404 ? undefined : resp.blob(),
                            fileExtension: url.value.split(".").pop(),
                        }) as File,
                ),
            ),
        );
        await exportBlobsToZip(documentBlobs, {
            folderName: `${fund.data.data.name} Documents (${dayjs().format(
                "DD-MM-YY",
            )})`,
        });
        //Mark all fund documents as read on bulk download
        //no easy way to do for individual docs
        markRead?.(fund.data.id);
        bulkDownloadEvent({
            fund: {
                name: fund.data.data.name,
                id: fund.data.id,
                type: fund.data.type,
            },
        });
    };
    return (
        <div className="mt-4 flex items-center justify-between gap-x-6 border-t border-gray-100 pt-4">
            <BulkDownloadCTA />
            <div className="flex items-center">
                {selectionActive && selectedDocumentURLs.length > 0 && (
                    <ClearAllCTA />
                )}
                <Button
                    icon={
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            className="mx-0.5 h-3 w-3 fill-current"
                        >
                            <path
                                d="M17,18.325l4.293-4.041a1.025,1.025,0,0,1,1.414,0,.944.944,0,0,1,0,1.367L16,22,9.293,15.65a.944.944,0,0,1,0-1.367,1.025,1.025,0,0,1,1.414,0L15,18.325V6.818A.923.923,0,0,1,16,6a.923.923,0,0,1,1,.818V18.325ZM7,24H25a3,3,0,0,1,0,6H7a3,3,0,0,1,0-6Zm9,2v2h9a1,1,0,0,0,0-2Z"
                                transform="translate(-4 -6)"
                            />
                        </svg>
                    }
                    className="ml-4 h-9 pl-3 pr-4 text-xs font-normal"
                    color="primary"
                    disabled={selectedDocumentURLs.length === 0}
                    onClick={handleDownload}
                >
                    <Translate>Download</Translate>
                </Button>
            </div>
        </div>
    );
};

const BulkDownloadCTA = () => {
    const { selectedDocumentURLs, toggleSelectionActive } =
        useDocumentContext();
    const numDocs = selectedDocumentURLs.length;
    const handleClick = (evt: React.MouseEvent) => {
        evt.stopPropagation();
        toggleSelectionActive();
    };
    return (
        <div className="mr-6 flex flex-wrap">
            <TextButton onClick={handleClick}>
                <Translate>Bulk download documents</Translate>
            </TextButton>
            <span className="text-battleship-grey ml-2 text-xs">
                {numDocs > 0 && (
                    <>
                        ({numDocs}{" "}
                        <Translate>
                            {numDocs > 1
                                ? "documents selected"
                                : "document selected"}
                        </Translate>
                        )
                    </>
                )}
            </span>
        </div>
    );
};

const ClearAllCTA = () => {
    const { setSelectedDocumentURLs } = useDocumentContext();
    const handleClearAll = (evt: React.MouseEvent) => {
        evt.stopPropagation();
        setSelectedDocumentURLs([]);
    };
    return (
        <TextButton onClick={handleClearAll}>
            <Translate>Clear all</Translate>
        </TextButton>
    );
};

export default DownloadsSection;
